import React from "react";
import {
  isThemePreference,
  persistThemePreference,
  readThemeCookie,
  resolveStoredThemePreference,
  THEME_PREFERENCES,
} from "./themePreferenceStorage.js";

export { THEME_PREFERENCES } from "./themePreferenceStorage.js";

const ThemePreferenceContext = React.createContext(null);

function getStoredPreference() {
  return resolveStoredThemePreference();
}

function resolveTheme(preference, mediaQuery) {
  return preference === "system" ? (mediaQuery.matches ? "dark" : "light") : preference;
}

function applyTheme(preference, mediaQuery) {
  const effectiveTheme = resolveTheme(preference, mediaQuery);
  const root = document.documentElement;
  root.classList.toggle("theme-dark", effectiveTheme === "dark");
  root.classList.toggle("theme-light", effectiveTheme === "light");
  root.dataset.themePreference = preference;
  root.style.colorScheme = effectiveTheme;
  return effectiveTheme;
}

export function ThemePreferenceProvider({ children }) {
  const initialPreference = React.useRef(getStoredPreference()).current;
  const [preference, setPreferenceState] = React.useState(initialPreference);
  const [effectiveTheme, setEffectiveTheme] = React.useState(() => resolveTheme(initialPreference, window.matchMedia("(prefers-color-scheme: dark)")));

  React.useEffect(() => {
    // Migrate a valid legacy localStorage preference when the shared cookie is absent.
    if (!isThemePreference(readThemeCookie())) persistThemePreference(initialPreference);
  }, [initialPreference]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const updateTheme = () => setEffectiveTheme(applyTheme(preference, mediaQuery));
    updateTheme();
    if (preference === "system") mediaQuery.addEventListener("change", updateTheme);
    return () => mediaQuery.removeEventListener("change", updateTheme);
  }, [preference]);

  React.useEffect(() => {
    const synchronizeFromCookie = () => {
      const nextPreference = getStoredPreference();
      setPreferenceState((current) => current === nextPreference ? current : nextPreference);
    };
    window.addEventListener("focus", synchronizeFromCookie);
    window.addEventListener("pageshow", synchronizeFromCookie);
    document.addEventListener("visibilitychange", synchronizeFromCookie);
    return () => {
      window.removeEventListener("focus", synchronizeFromCookie);
      window.removeEventListener("pageshow", synchronizeFromCookie);
      document.removeEventListener("visibilitychange", synchronizeFromCookie);
    };
  }, []);

  const setPreference = React.useCallback((nextPreference) => {
    if (!isThemePreference(nextPreference)) return;
    persistThemePreference(nextPreference);
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setEffectiveTheme(applyTheme(nextPreference, mediaQuery));
    setPreferenceState(nextPreference);
  }, []);

  const value = React.useMemo(() => ({ preference, effectiveTheme, setPreference }), [preference, effectiveTheme, setPreference]);
  return <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>;
}

export function useThemePreference() {
  const context = React.useContext(ThemePreferenceContext);
  if (!context) throw new Error("useThemePreference must be used inside ThemePreferenceProvider");
  return context;
}
